import type { StepIds } from '~/components/premarketplace/partner-config';

// Validated, narrowed domain type for the `visibleWhen` branching contract
// (INT-1294). The GraphQL layer exposes the raw wire types (StepVisibilityCondition
// / StepVisibilityClause in ~/gql/graphql) with plain-string operator/questionId;
// parseStepFlow validates those into these narrowed types (known operators, StepIds).
export type VisibleWhenOperator = 'equals' | 'notEquals' | 'in' | 'notIn';

export type VisibleWhenClause =
  | {
      // stepFlow.id of a PRIOR step whose answer is tested.
      questionId: StepIds;
      operator: VisibleWhenOperator;
      values: string[];
    }
  | {
      // A nested condition can be combined with flat predicate clauses, preserving
      // compatibility with the existing configuration format.
      match?: 'all' | 'any';
      clauses: VisibleWhenClause[];
    };

export type VisibleWhen = {
  // How clauses combine: 'all' = AND (default), 'any' = OR.
  match?: 'all' | 'any';
  clauses: VisibleWhenClause[];
};

/**
 * Evaluate a single clause against the set of selected option-values for its
 * `questionId`. A missing answer is treated as the empty set — so positive
 * operators (`equals`/`in`) are false and negative operators (`notEquals`/`notIn`)
 * are true. The orchestrator passes answers only for already-shown prior steps, so
 * a hidden or not-yet-shown step's answer never leaks into a later step's
 * visibility evaluation.
 */
export function evaluateClause(
  clause: Exclude<VisibleWhenClause, { clauses: VisibleWhenClause[] }>,
  answerSet: Set<string>,
): boolean {
  const { operator, values } = clause;
  switch (operator) {
    case 'equals':
      return answerSet.size === 1 && answerSet.has(values[0]);
    case 'notEquals':
      return !(answerSet.size === 1 && answerSet.has(values[0]));
    case 'in':
      return values.some((value) => answerSet.has(value));
    case 'notIn':
      return !values.some((value) => answerSet.has(value));
    default:
      return false;
  }
}

/**
 * Evaluate a whole condition against the accumulated answers. Clauses combine
 * with `match` ('all' = AND, the default; 'any' = OR). A clause may itself be a
 * condition, allowing `(A AND B) OR C` while retaining compatibility with flat
 * clauses. A condition with no clauses is treated as "shown" so malformed config
 * never hides a step.
 */
export function evaluateVisibleWhen(visibleWhen: VisibleWhen, answers: ReadonlyMap<StepIds, Set<string>>): boolean {
  const { clauses, match } = visibleWhen;
  if (!clauses?.length) return true;

  const passes = (clause: VisibleWhenClause) => {
    if ('clauses' in clause) return evaluateVisibleWhen(clause, answers);
    return evaluateClause(clause, answers.get(clause.questionId) ?? new Set<string>());
  };

  return match === 'any' ? clauses.some(passes) : clauses.every(passes);
}
